import { client } from './sanity';
import {
  allBlogsQuery,
  latestBlogsQuery,
  allCategoriesQuery,
  blogsByCategoryQuery,
  blogBySlugQuery,
  categoryBySlugQuery,
  buildPaginatedBlogsQuery,
  buildTotalBlogsCountQuery,
} from './queries';
import { Blog, Category } from '@/components/type/type';

export type SortOption = 'most-recent' | 'oldest';

export interface GetPaginatedBlogsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  categoryId?: string | null; // null/'all' => no category filter
  sortBy?: SortOption;
}

// Fetch all blogs (unfiltered)
export async function getAllBlogs(): Promise<Blog[]> {
  try {
    const blogs = await client.fetch(allBlogsQuery);
    return blogs || [];
  } catch (error) {
    console.error('Error fetching all blogs:', error);
    return [];
  }
}

// Fetch paginated blogs with server-side search, category filter, and sort
export async function getPaginatedBlogs({
  page = 1,
  pageSize = 12,
  search = '',
  categoryId = null,
  sortBy = 'most-recent',
}: GetPaginatedBlogsParams): Promise<{
  blogs: Blog[];
  total: number;
  totalPages: number;
}> {
  try {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    // Normalize params for GROQ: empty string/'all' -> null so the
    // "($x == null || ...)" guard clauses in the query short-circuit correctly.
    const params = {
      start,
      end,
      search: search ? `*${search}*` : null,
      categoryId: categoryId && categoryId !== 'all' ? categoryId : null,
    };

    const paginatedQuery = buildPaginatedBlogsQuery(sortBy);
    const countQuery = buildTotalBlogsCountQuery();

    const [blogs, total] = await Promise.all([
      client.fetch(paginatedQuery, params),
      client.fetch(countQuery, params),
    ]);

    const totalPages = Math.ceil((total || 0) / pageSize);

    return {
      blogs: blogs || [],
      total: total || 0,
      totalPages,
    };
  } catch (error) {
    console.error('Error fetching paginated blogs:', error);
    return { blogs: [], total: 0, totalPages: 0 };
  }
}

// Fetch latest blogs (marked as isLatestBlog = true)
export async function getLatestBlogs(): Promise<Blog[]> {
  try {
    const blogs = await client.fetch(latestBlogsQuery);
    return blogs || [];
  } catch (error) {
    console.error('Error fetching latest blogs:', error);
    return [];
  }
}

// Fetch all categories
export async function getAllCategories(): Promise<Category[]> {
  try {
    const categories = await client.fetch(allCategoriesQuery);
    return categories || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
}

// Fetch blogs by category
export async function getBlogsByCategory(categoryId: string): Promise<Blog[]> {
  try {
    const blogs = await client.fetch(blogsByCategoryQuery, { categoryId });
    return blogs || [];
  } catch (error) {
    console.error('Error fetching blogs by category:', error);
    return [];
  }
}

// Fetch single category by slug
export async function getCategoryBySlug(
  slug: string,
): Promise<Category | null> {
  try {
    const category = await client.fetch(categoryBySlugQuery, { slug });
    return category || null;
  } catch (error) {
    console.error('Error fetching category by slug:', error);
    return null;
  }
}

// Fetch blogs by category slug
export async function getBlogsByCategorySlug(slug: string): Promise<Blog[]> {
  try {
    const category = await client.fetch(categoryBySlugQuery, { slug });

    if (!category) {
      console.error('Category not found for slug:', slug);
      return [];
    }
    const blogs = await client.fetch(
      `*[_type == "blog" && references($categoryId)] | order(publishedAt desc, _createdAt desc) {
        _id,
        title,
        slug,
        excerpt,
        mainImage,
        readTime,
        isLatestBlog,
        publishedAt,
        _createdAt,
        _updatedAt,
        seo{
          title,
          description,
          image
        },
        author->{
          _id,
          name,
          slug,
          image,
          socialMedia[]->{
            _id,
            platform,
            url,
            icon,
            displayOrder,
            isActive
          }
        },
        categories[]->{
          _id,
          title,
          slug,
          description
        },
        body
      }`,
      { categoryId: category._id },
    );

    console.log('Found blogs:', blogs?.length || 0);
    return blogs || [];
  } catch (error) {
    console.error('Error fetching blogs by category slug:', error);
    return [];
  }
}

// Fetch single blog by slug
export async function getBlogBySlug(slug: string): Promise<Blog | null> {
  try {
    const blog = await client.fetch(
      blogBySlugQuery,
      { slug },
      { cache: 'no-store' },
    );
    return blog || null;
  } catch (error) {
    console.error('Error fetching blog by slug:', error);
    return null;
  }
}
